import { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import useManufacturers from "@/hooks/useManufacturers";
import { Check, ChevronsUpDown, Package, Search, X } from "lucide-react";

/**
 * Multi-select dropdown for products with search + checkbox list.
 *
 * Props:
 *   value      — array of selected product ids
 *   onChange   — (ids: string[]) => void
 *   placeholder
 *   testId
 *   disabled
 */
export default function MultiSelectProducts({
  value = [],
  onChange,
  placeholder = "Select products…",
  testId = "multi-select-products",
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const mfrs = useManufacturers();
  const mfrById = useMemo(() => mfrs.reduce((a, m) => { a[m.id] = m.name; return a; }, {}), [mfrs]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .get("/products")
      .then((r) => {
        if (!cancelled) setProducts(r.data || []);
      })
      .catch(() => {
        if (!cancelled) setProducts([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  const selectedSet = useMemo(() => new Set(value), [value]);

  const filtered = useMemo(() => {
    const sorted = [...products].sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    if (!q.trim()) return sorted;
    const needle = q.toLowerCase();
    // Search across name, SKU, category, supplier AND company (OEM) name so
    // typing e.g. "fotric" surfaces every FOTRIC product.
    return sorted.filter(
      (p) =>
        (p.name || "").toLowerCase().includes(needle) ||
        (p.sku || "").toLowerCase().includes(needle) ||
        (p.category || "").toLowerCase().includes(needle) ||
        (p.supplier || "").toLowerCase().includes(needle) ||
        (mfrById[p.manufacturer_id] || "").toLowerCase().includes(needle)
    );
  }, [q, products, mfrById]);

  const toggle = (id) => {
    if (selectedSet.has(id)) onChange(value.filter((v) => v !== id));
    else onChange([...value, id]);
  };

  const remove = (id, e) => {
    e?.stopPropagation();
    e?.preventDefault();
    onChange(value.filter((v) => v !== id));
  };

  const selectedProducts = useMemo(
    () => products.filter((p) => selectedSet.has(p.id)),
    [products, selectedSet]
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className="w-full justify-between rounded-2xl h-auto min-h-11 px-3 py-2 mt-1.5 font-normal hover:bg-muted/40"
          data-testid={testId}
        >
          <div className="flex flex-wrap gap-1.5 flex-1 items-center">
            {selectedProducts.length === 0 ? (
              <span className="text-muted-foreground inline-flex items-center gap-2">
                <Package className="h-4 w-4" /> {placeholder}
              </span>
            ) : (
              selectedProducts.map((p) => (
                <Badge
                  key={p.id}
                  variant="outline"
                  className="bg-primary-50 text-primary-700 border-primary-200 gap-1 pr-1 max-w-full"
                  data-testid={`${testId}-chip-${p.id}`}
                >
                  <span className="truncate max-w-[14rem]">{p.name}</span>
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={(e) => remove(p.id, e)}
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") remove(p.id, e); }}
                    className="hover:bg-primary-100 rounded-full p-0.5 -mr-0.5 cursor-pointer"
                    aria-label={`Remove ${p.name}`}
                    data-testid={`${testId}-remove-${p.id}`}
                  >
                    <X className="h-3 w-3" />
                  </span>
                </Badge>
              ))
            )}
          </div>
          <ChevronsUpDown className="h-4 w-4 opacity-50 shrink-0 ml-2" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[--radix-popover-trigger-width] p-0 rounded-2xl"
        align="start"
        data-testid={`${testId}-popover`}
      >
        <div className="p-2 border-b border-border/60">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search products…"
              className="pl-9 h-9 rounded-xl bg-muted/40 border-0"
              data-testid={`${testId}-search`}
            />
          </div>
        </div>
        <div className="max-h-64 overflow-y-auto overscroll-contain">
          <div className="p-1">
            {loading ? (
              <p className="py-6 text-center text-xs text-muted-foreground">Loading…</p>
            ) : filtered.length === 0 ? (
              <p className="py-6 text-center text-xs text-muted-foreground">
                {products.length === 0 ? "No products in catalog yet." : "No matches."}
              </p>
            ) : (
              filtered.map((p) => {
                const checked = selectedSet.has(p.id);
                return (
                  <button
                    type="button"
                    key={p.id}
                    onClick={() => toggle(p.id)}
                    className={`flex items-start gap-2.5 w-full px-2.5 py-2 rounded-xl tap text-left transition ${
                      checked ? "bg-primary-50/60" : "hover:bg-muted/50"
                    }`}
                    data-testid={`${testId}-option-${p.id}`}
                  >
                    <span
                      aria-hidden="true"
                      className={`mt-0.5 h-4 w-4 rounded border grid place-items-center shrink-0 ${
                        checked
                          ? "bg-primary border-primary text-primary-foreground"
                          : "border-input bg-background"
                      }`}
                    >
                      {checked && <Check className="h-3 w-3" strokeWidth={3} />}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold truncate">{p.name}</p>
                      {p.category && (
                        <p className="text-[11px] text-muted-foreground truncate">{p.category}</p>
                      )}
                    </div>
                    {checked && <Check className="h-4 w-4 text-primary mt-0.5 shrink-0" />}
                  </button>
                );
              })
            )}
          </div>
        </div>
        {selectedProducts.length > 0 && (
          <div className="px-3 py-2 border-t border-border/60 flex items-center justify-between">
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">
              {selectedProducts.length} selected
            </span>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => onChange([])}
              className="h-7 px-2 text-xs rounded-xl"
              data-testid={`${testId}-clear`}
            >
              Clear
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
